import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import { CheckCircle2, AlertTriangle, Info, X } from 'lucide-react';
import { cn } from '../utils';

export type ToastTone = 'info' | 'success' | 'warning' | 'error';

export interface ToastInput {
  title: string;
  description?: string;
  tone?: ToastTone;
  /** Auto-dismiss after this many ms (default 4000). Set 0 to disable. */
  duration?: number;
}

interface ToastInternal extends ToastInput {
  id: string;
}

interface ToastContextValue {
  toast: (t: ToastInput) => void;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>');
  return ctx;
}

const TONE_ICON: Record<ToastTone, ReactNode> = {
  info: <Info size={14} />,
  success: <CheckCircle2 size={14} />,
  warning: <AlertTriangle size={14} />,
  error: <AlertTriangle size={14} />,
};

const TONE_ACCENT: Record<ToastTone, string> = {
  info: 'text-ink-700',
  success: 'text-[color:var(--color-emerald)]',
  warning: 'text-[color:var(--color-gold-600)]',
  error: 'text-[color:var(--color-rose)]',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastInternal[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((cur) => cur.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback((t: ToastInput) => {
    const id = `t_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    setToasts((cur) => [...cur, { ...t, id }]);
  }, []);

  const value = useMemo(() => ({ toast, dismiss }), [toast, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {typeof document !== 'undefined' &&
        createPortal(
          <div className="fixed bottom-4 right-4 z-[200] flex flex-col gap-2 pointer-events-none">
            <AnimatePresence>
              {toasts.map((t) => (
                <ToastItem key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
              ))}
            </AnimatePresence>
          </div>,
          document.body,
        )}
    </ToastContext.Provider>
  );
}

function ToastItem({
  toast,
  onDismiss,
}: {
  key?: string;
  toast: ToastInternal;
  onDismiss: () => void;
}) {
  const tone = toast.tone ?? 'info';
  const duration = toast.duration ?? 4000;

  useEffect(() => {
    if (duration <= 0) return;
    const timer = window.setTimeout(onDismiss, duration);
    return () => window.clearTimeout(timer);
  }, [duration, onDismiss]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 8, scale: 0.98 }}
      transition={{ duration: 0.22 }}
      className="pointer-events-auto bg-white rounded-xl shadow-pop border border-ink-100 w-[320px] p-3 flex items-start gap-3"
      role="status"
    >
      <span className={cn('shrink-0 mt-0.5', TONE_ACCENT[tone])}>{TONE_ICON[tone]}</span>
      <div className="flex-1 min-w-0">
        <p className="text-[12.5px] font-semibold text-ink-900 leading-tight">{toast.title}</p>
        {toast.description && (
          <p className="text-[11.5px] text-ink-500 mt-0.5 leading-snug">{toast.description}</p>
        )}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="shrink-0 grid place-items-center w-6 h-6 rounded-md text-ink-400 hover:text-ink-900 hover:bg-ink-100 cursor-pointer focus-ring"
      >
        <X size={12} />
      </button>
    </motion.div>
  );
}
